<template>
  <div v-if="sharedMap?.id">
    <v-dialog
      v-model="dialog"
      width="auto"
      @click:outside="closeDialog()"
    >
      <v-card class="card br-5 pt-1">
        <v-toolbar color="rgba(0, 0, 0, 0)">
          <v-toolbar-title class="card__title">
            Share "{{ sharedMap.name }}"
          </v-toolbar-title>
          <template #append>
            <v-btn
              variant="text"
              icon="mdi-close"
              @click="closeDialog"
            />
          </template>
        </v-toolbar>
        <v-card-text class="card__body pt-2">
          <v-autocomplete
            v-if="!disableInputs"
            ref="searchInput"
            v-model="selectedUser"
            item-title="name"
            :items="searchableUsers"
            :custom-filter="filterUsers"
            variant="outlined"
            placeholder="Add people"
            class="card__body__input"
            :class="{'card__body__input-hidden': selectedUser}"
            menu-icon=""
            return-object
            @update:model-value="updateUsersWithAccess"
          >
            <template #item="{ props, item }">
              <v-list-item
                v-bind="props"
                :title="`${item.raw.displayName} (${item.raw.email})`"
              />
            </template>
          </v-autocomplete>
          <div class="card__body__header">
            People with access
          </div>
          <v-row>
            <v-col class="card__body__user-list pt-0">
              <ShareSettingsRow
                :title="`${sharedMap.createdOn?.name} (${getUserEmail(sharedMap.createdOn?.uid)})`"
                role="Owner"
                style="padding: 10px;"
                :disabled="true"
              >
                <template #icon>
                  <div class="card__body__user-icon">
                    <span>{{ userInitials(sharedMap.createdOn?.name) }}</span>
                  </div>
                </template>
              </ShareSettingsRow>
              <ShareSettingsRow
                v-for="user in usersWithAccess"
                :key="user.uid"
                :title="`${user.displayName} (${user.email})`"
                role="Editor"
                :deleteable="true"
                :disabled="disableInputs"
                style="padding: 10px;"
                @removeUser="() => updateUsersWithAccess(user, 'remove')"
                @mouseenter="highlightRow($event)"
                @mouseleave="removeHighlightRow($event)"
              >
                <template #icon>
                  <div class="card__body__user-icon">
                    <span>{{ userInitials(user.displayName) }}</span>
                  </div>
                </template>
              </ShareSettingsRow>
            </v-col>
          </v-row>
          <div
            class="card__body__header"
            style="margin-top: 15px"
          >
            General access
          </div>
          <v-row>
            <v-col class="pa-0">
              <ShareSettingsRow
                title="NorthPoint Development"
                :role="npAccessRole"
                :role-options="['Editor', 'Viewer']"
                :disabled="disableInputs"
                style="padding: 0px 10px;"
                @updateRole="(event) => updateRole(event, 'northpoint')"
              >
                <template #icon>
                  <OrangeCircle
                    :height="'44px'"
                    :width="'44px'"
                    class="card__body__np-icon"
                  />
                </template>
                <template #subtitle>
                  <div class="text-subtitle-2">
                    <span v-if="sharedMap?.share?.accessLevel === 'edit'">Anyone at NorthPoint can edit</span>
                    <span v-else>Anyone at NorthPoint can view</span>
                  </div>
                </template>
              </ShareSettingsRow>
              <ShareSettingsRow
                title="Anyone with the link"
                :role="publicAccessRole"
                :role-options="['Restricted', 'Viewer']"
                :disabled="disableInputs"
                style="padding: 0px 10px;"
                @updateRole="(event) => updateRole(event, 'public')"
              >
                <template #icon>
                  <div class="card__body__public">
                    <GlobeIcon class="card__body__public-icon" />
                  </div>
                </template>
                <template #subtitle>
                  <div
                    v-if="sharedMap?.share?.accessType === 'link'"
                    class="text-subtitle-2"
                  >
                    <span class="font-weight-bold bg-yellow mr-1">
                      Review the map for sensitive information before sharing.
                    </span>
                    Examples: Lease Deals, lease terms, investors, debt, sites in pursuit, etc.
                    <span class="font-weight-bold">
                      Anyone
                    </span>
                    with this link can view the map.
                  </div>
                  <div
                    v-else
                    class="text-subtitle-2"
                  >
                    Unauthenticated access is disabled
                  </div>
                </template>
              </ShareSettingsRow>
            </v-col>
          </v-row>
          <v-row
            justify="space-between"
            class="pt-3"
          >
            <v-col class="d-flex justify-start">
              <v-btn
                variant="outlined"
                :color="copyLinkColor"
                prepend-icon="mdi-link"
                @click="copyToClipboard(getLink('view'))"
              >
                Copy Link
              </v-btn>
            </v-col>
            <v-col
              v-if="showRedirectBtn"
              class="d-flex justify-end"
            >
              <np-action-btn @click="handleRedirect">
                Switch to {{ redirectMode }} Mode
              </np-action-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      timeout="1000"
    >
      Copied to clipboard!
    </v-snackbar>
  </div>
</template>

<script>
// Services
import {MapService} from '@/services/MapService';
import {currentUser, getDocument} from '@/services/FirestoreService';
import {createAuditRecord, getSharedMapsUsers} from '@/services/BackendService';
import {decompressBase64ToObject} from '@northpoint-development/labs-common-core';
import {mapMutations, mapState, mapGetters} from 'vuex';

// Components
import ShareSettingsRow from './components/ShareSettingsRow.vue';
import OrangeCircle from '@/components/svgs/MarkerIcons/Circles/OrangeCircle.vue';
import GlobeIcon from '@/components/svgs/GlobeIcon.vue';

/**
 * Represents a User from Firebase.
 * @typedef {Object} User
 * @property {string} email
 * @property {string} uid
 * @property {string} displayName
 */

export default {
  name: 'ShareDialog',
  components: {
    ShareSettingsRow,
    OrangeCircle,
    GlobeIcon,
  },
  props: {
    mapId: {
      type: String,
      default: '',
    },
    showDialog: {
      type: Boolean,
      required: true,
    },
    isMapView: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'close'],
  data() {
    return {
      copyLinkColor: '#0B57D0',
      selectedUser: null,
      snackbar: false,
      zoom: null,
      latLng: null,
      disableInputs: true,
      usersWithAccess: [],
      /** @type {User[]} */
      users: [],
    };
  },
  computed: {
    ...mapState(['sharedMap']),
    ...mapGetters(['userCanEditMap']),
    dialog: {
      get() {
        return this.showDialog;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    searchableUsers() {
      return this.users.filter((user) =>
        (!this.sharedMap?.share?.users?.includes(user.uid) && user.uid !== this.sharedMap?.createdOn?.uid));
    },
    npAccessRole() {
      if (this.sharedMap?.share?.accessLevel === 'edit') {
        return 'Editor';
      }
      return 'Viewer';
    },
    publicAccessRole() {
      if (this.sharedMap?.share?.accessType === 'link') {
        return 'Viewer';
      }
      return 'Restricted';
    },
    showRedirectBtn() {
      const inDashboard = ['#user', '#org'].some((dashboardTab) => window.location.href.includes(dashboardTab));
      return (window.location.pathname.includes('edit') || this.userCanEditMap) && !inDashboard;
    },
    redirectMode() {
      return window.location.pathname.includes('edit') ? 'View' : 'Edit';
    },
  },
  watch: {
    async showDialog(value) {
      if (value) {
        const user = currentUser();
        await this.fetchUsers();

        // Initialize the shared map if it has not been set
        if (!this.sharedMap?.id) {
          try {
            const map = await getDocument('sharedMaps', this.mapId);
            map.folders = await decompressBase64ToObject(map.folders);
            this.setSharedMap({id: this.mapId, editMode: user?.uid !== undefined, ...map});
          } catch (error) {
            error.message = `Error getting shared map: ${error.message}`;
            console.error(error);
          }
        }

        // Filter the users with access to the shared map
        this.usersWithAccess = this.users.filter((user) => this.sharedMap?.share?.users?.includes(user.uid));

        // Disable all inputs if the user is not authorized to edit
        this.disableInputs = !this.userCanEditMap;
      }
    },
  },
  updated() {
    if (MapService.isMapInitialized()) {
      // This is within update() to avoid an idle listener - the information becomes stale otherwise
      const {zoom, latLng} = this.getMapZoomAndCenter();
      this.zoom = zoom;
      this.latLng = latLng;
    }
  },
  methods: {
    ...mapMutations(['setSharedMap']),
    /**
     * Update the users with access to the shared map and trigger a Firestore update
     * @param {object} selectedUser - The user object to add or remove
     * @param {'add' | 'remove'} action - The action to take, either 'add' or 'remove'
     */
    async updateUsersWithAccess(selectedUser, action) {
      // Ignore the update if the selected user is not valid
      if (!selectedUser?.uid) return;

      let auditEventType;
      const auditEventPayload = {};
      if (action === 'remove') {
        this.usersWithAccess = this.usersWithAccess.filter((user) => user.uid !== selectedUser.uid);
        auditEventType = 'shareSettings-removedEditor';
        auditEventPayload.removedEditor = {
          uid: selectedUser.uid,
          name: selectedUser.displayName,
        };
      } else {
        const user = this.users.find((user) => user.uid === selectedUser.uid);
        this.usersWithAccess.push(user);
        auditEventType = 'shareSettings-addedEditor';
        auditEventPayload.addedEditor = {
          uid: selectedUser.uid,
          name: selectedUser.displayName,
        };
      }
      // Insert audit record for adding or removing a user
      createAuditRecord(auditEventType, auditEventPayload);

      // Update the shared map with the new share.users array
      await this.sharedMap.update({
        share: {
          ...this.sharedMap.share,
          users: this.usersWithAccess.map((user) => user.uid),
        },
      });

      // Clear the search input
      this.selectedUser = null;
      this.$refs.searchInput.reset();
    },
    handleRedirect() {
      window.location.href = window.location.href.replace(/edit|view/, this.redirectMode.toLowerCase());
    },
    async fetchUsers() {
      if (this.users > 0) return;

      try {
        this.users = await getSharedMapsUsers();
      } catch (error) {
        error.message = 'Error fetching users: ' + error.message;
        console.error(error);
      }
    },
    /**
     * Update a share role value for the shared map and trigger a Firestore update
     * @param {'Editor' | 'Viewer' | 'Restricted'} role - 'Editor' or 'Viewer' for northpoint,
     * 'Restricted' or 'Viewer' for public
     * @param {'northpoint' | 'public'} fieldName - The field to update, either 'northpoint' or 'public'
     */
    updateRole(role, fieldName) {
      if (!role || !fieldName) return;

      let auditEventType = 'shareSettings-changedTo';
      // Update the shared map with the new share.accessLevel or share.accessType
      if (fieldName === 'northpoint') {
        auditEventType += 'Np';
        this.sharedMap.update({
          share: {
            ...this.sharedMap.share,
            accessLevel: role === 'Editor' ? 'edit' : 'view',
          },
        });
      } else {
        auditEventType += 'Anyone';
        this.sharedMap.update({
          share: {
            ...this.sharedMap.share,
            accessType: role === 'Viewer' ? 'link' : 'restricted',
          },
        });
      }
      auditEventType += {
        Editor: 'Edit',
        Viewer: 'View',
        Restricted: 'Restricted',
      }[role];
      // Insert audit record for changing the access level
      createAuditRecord(auditEventType);

      // Send a warning email if the public access level is set to 'Viewer'
      if (fieldName === 'public' && role === 'Viewer') {
        this.sendWarningEmail();
      }
    },
    /**
     * Filter the users based on the query text
     * @param {string} value - The value of the item
     * @param {string} queryText - The query text to filter by
     * @param {object} item - The item to filter
     * @return {boolean} - True if the item matches the query text, false otherwise
     */
    filterUsers(value, queryText, item) {
      const name = item.raw.displayName.toLowerCase();
      const email = item.raw.email.toLowerCase();
      return name.includes(queryText.toLowerCase()) || email.includes(queryText.toLowerCase());
    },
    /**
     * Returns the email of the user with the matching UID.
     * @param {string} userId The UID of the user to find.
     * @return {string} The matching user's email.
     */
    getUserEmail(userId) {
      return this.users.find((user) => user.uid === userId)?.email;
    },
    getLink(type) {
      // 'localhost:9090' or 'maps.northpointkc.com'
      const origin = window.location.origin;

      // '/beta', '/release', or '/'.
      // If the public path is '/', set it to an empty string so it doesn't duplicate the '/'
      const subdirectory = process.env.VUE_APP_PUBLIC_PATH === '/' ? '' : process.env.VUE_APP_PUBLIC_PATH;

      // Get parameters if the zoom exists
      const params = this.zoom ? `?zoom=${this.zoom}&lat=${this.latLng?.lat()}&lng=${this.latLng?.lng()}` : '';

      // origin/subdirectory/mapId/view?params
      return `${origin}${subdirectory}/${this.sharedMap.id}/${type}${params}`;
    },
    copyToClipboard(link) {
      navigator.clipboard.writeText(link);
      this.snackbar = true;
    },
    getMapZoomAndCenter() {
      const zoom = MapService.getMap().getZoom();
      const latLng = MapService.getMap().getCenter();
      return {zoom, latLng};
    },
    userInitials(name) {
      // Return the first letter of the first word and the last letter of the last word
      const initials = name.split(' ').map((word) => word[0]);
      return initials[0].concat(initials[initials.length-1]);
    },
    highlightRow(event) {
      if (!this.disableInputs) {
        event.target.classList.add('highlighted');
      }
    },
    removeHighlightRow(event) {
      if (!this.disableInputs) {
        event.target.classList.remove('highlighted');
      }
    },
    closeDialog() {
      this.selectedUser = null;
      // If in DashboardView, reset the shared map
      if (!this.isMapView) {
        this.setSharedMap({});
      }
      this.$emit('close');
    },
    /**
     * Sends a warning email when the map setting for 'Anyone with the link' is set to 'Viewer'
     * Only sends the email once per map
     */
    async sendWarningEmail() {
      // Only one warning email should be sent for the map
      if (this.sharedMap.hasSentWarningEmail) return;

      let cloudFunctionUrl;
      if (process.env.VUE_APP_GCP_ENV === 'production') {
        cloudFunctionUrl = `https://us-central1-shareablemaps-prod.cloudfunctions.net/sendEmail`;
      } else {
        cloudFunctionUrl = `https://us-central1-shareablemaps-dev.cloudfunctions.net/sendEmail-staging`;
      }
      const user = currentUser();
      const idToken = await user.getIdToken();
      const body = JSON.stringify({
        emailType: 'view-mode-warning',
        map: {
          id: this.sharedMap.id,
          name: this.sharedMap.name,
          creator: this.sharedMap.createdOn?.name,
          currentUserName: user.displayName,
        },
      });

      // Invoke the cloud function to send the email
      const response = await fetch(cloudFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body,
      });
      const json = await response.json();

      if (json?.error) {
        console.error(json.error);
        return;
      }

      // Update the shared map to prevent multiple emails
      await this.sharedMap.update({hasSentWarningEmail: true});
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card {
  overflow-y: hidden !important;
}
.card {
  &__title {
    color: main.$npGray;
    padding: 10px;
    padding-top: 15px;
    padding-left: 3px;
    font-weight: 500;
    font-size: 1.5rem;
  }

  &__body {
    @include main.respond(desktop) {
      width: 50vw;
    }

    &__header {
      cursor: default;
      font-size: 1.15rem;
      font-weight: 500;
      margin-bottom: 15px;
    }

    &__user-list {
      color: main.$npGray;
      padding: 0px;
      max-height: 209px;
      overflow-y: auto;
      scroll-behavior: smooth;
      margin-bottom: 5px;
    }

    &__input {
      color: black;
      &-hidden {
        :deep(.v-field__input) {
          color: #fff;
        }
      }
    }

    &__user-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: lightblue;
    }

    &__np-icon {
      margin: 0px -2px;
      width: 32px;
      height: 32px;
    }

    &__public {
      background-color: rgb(174, 237, 174);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: -2px;
      &-icon {
        width: 32px;
        height: 32px;
        padding: 5px;
      }
    }
  }
}
.highlighted {
  background-color: rgba(151, 151, 151, 0.1);
}
</style>
